import { APITypes } from "@cur8/api-client";
import { useSnackbar } from "notistack";
import { useCallback, useState } from "react";
import { useGiveStudyConsentMutation } from "render/hooks/mutations/consent/useGiveStudyConsentMutation";
import { useRevokeStudyConsentMutation } from "render/hooks/mutations/consent/useRevokeStudyConsentMutation";
import { useAsyncHandle } from "render/hooks/useAsyncHandle";
import { ReactComponent as ArrowIcon } from "render/ui/icons/arrow.svg";
import { BurgerLayout } from "render/ui/layout/BurgerLayout";
import { FullScreenPageLayout } from "render/ui/layout/FullScreenPageLayout";
import { LogoHeader } from "render/ui/layout/LogoHeader";
import { IconButton } from "render/ui/trigger/IconButton";
import PatientJournalContent from "render/views/ProfileView/components/ConsentDocument/components/PatientJournalContent";
import StudyContent from "render/views/ProfileView/components/ConsentDocument/components/StudyContent";
import { useConsentsMap } from "render/views/ProfileView/components/DataAndPrivacySection/hooks/useConsentsMap";
import styles from "./styles.module.sass";
import { Trans } from "./trans";

interface ConsentDocumentProps {
  allowEdit?: boolean;
  onClose: () => void;
  onToggle: () => void;
  documentUrl: string;
  study?: APITypes.ConsentSummary;
}

export function ConsentDocument({
  allowEdit = true,
  onClose,
  study,
  documentUrl,
  onToggle,
}: ConsentDocumentProps) {
  const { other } = useConsentsMap();
  const giveStudyConsentMutation = useGiveStudyConsentMutation();
  const revokeStudyConsentMutation = useRevokeStudyConsentMutation();
  const isPartOfStudy = study != null;
  const { enqueueSnackbar } = useSnackbar();
  const [checked, setChecked] = useState(isPartOfStudy);

  const { busy, callback } = useAsyncHandle(async (checked: boolean) => {
    if (checked) {
      try {
        await giveStudyConsentMutation({
          documentUri: documentUrl,
          consentSignature: {
            $type: "login",
          },
        });
        onToggle();
      } catch (e) {
        enqueueSnackbar(<Trans.RemoveConsentError />, {
          variant: "error",
        });
      }
    } else {
      try {
        await revokeStudyConsentMutation(study!.id!);
        onToggle();
      } catch (e) {
        enqueueSnackbar(<Trans.ConsentError />, {
          variant: "error",
        });
      }
    }
  });

  const onClick = useCallback(
    (val: boolean) => {
      if (busy) {
        return;
      }
      setChecked(val);
      callback(val)?.catch(() => {
        setChecked(!val);
      });
    },
    [busy, callback]
  );

  return (
    <FullScreenPageLayout>
      <BurgerLayout>
        <div className={styles.header}>
          <LogoHeader
            leftElement={
              <IconButton
                ariaLabel={Trans.Arrow()}
                onClick={onClose}
                icon={<ArrowIcon />}
              />
            }
          />
        </div>
        <div data-hj-suppress className={styles.body}>
          {documentUrl === other.PATIENTJOURNAL ? (
            <PatientJournalContent
              onClick={onClick}
              developmentAndTestingLink={other.DEVELOPMENTANDTESTING}
              allowEdit={allowEdit}
              checked={checked}
              busy={busy}
              study={study}
            />
          ) : (
            <StudyContent
              documentUrl={documentUrl}
              allowEdit={allowEdit}
              checked={checked}
              busy={busy}
              onClick={onClick}
              study={study}
            />
          )}
        </div>
        {null}
      </BurgerLayout>
    </FullScreenPageLayout>
  );
}
